import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@lib/api/client';
import { CTA, METRIC_LABEL } from '@components/ui/designTokens';
import { captureScreenMap } from '../lib/captureScreenMap';
import { normalize, resolveRoute, visibleScreens } from '../lib/screens';
import { Markdown } from './Markdown';

export interface GuidedStep {
  action: string; // navigate | highlight | fill | click | info
  target?: string; // nombre semántico (data-guide) o ruta para navigate
  value?: string;
  label?: string;
}

const isVisible = (e: HTMLElement) => e.offsetParent !== null;

// Etiqueta "visible" de un elemento para matchear contra el target del agente.
// Para campos (input/select/textarea) NO usamos textContent (trae todas las
// opciones); usamos aria-label / placeholder / <label for> / name. Para
// botones/links: el texto, o aria-label/title/data-guide si es solo-ícono.
function labelOf(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'select' || tag === 'textarea') {
    const id = el.getAttribute('id');
    const byLabel = id ? document.querySelector(`label[for="${id}"]`)?.textContent || '' : '';
    return normalize(
      el.getAttribute('aria-label') || el.getAttribute('placeholder') || byLabel || el.getAttribute('name') || ''
    );
  }
  const text = normalize(el.textContent || '');
  if (text) return text;
  return normalize(el.getAttribute('aria-label') || el.getAttribute('title') || el.getAttribute('data-guide') || '');
}

// Busca el target dentro de un root con matching TOLERANTE: data-guide exacto →
// mejor candidato (inputs/selects/botones/links) por igualdad, substring en AMBOS
// sentidos (el agente suele describir de más, ej. target="Seleccione la dirección
// del receptor" vs campo "Seleccione la dirección") y, como último recurso,
// solapamiento de palabras. Prioriza elementos visibles y el match más específico.
function matchIn(root: ParentNode, n: string): HTMLElement | null {
  const byGuide = Array.from(root.querySelectorAll<HTMLElement>('[data-guide]')).filter(
    (e) => normalize(e.getAttribute('data-guide') || '') === n
  );
  const g = byGuide.find(isVisible) || byGuide[0];
  if (g) return g;

  const candidates = Array.from(
    root.querySelectorAll<HTMLElement>(
      'a, button, [role="button"], [role="link"], [role="combobox"], [role="menuitem"], input, select, textarea'
    )
  );
  const nTokens = new Set(n.split(/\s+/).filter((w) => w.length > 2));

  let best: { el: HTMLElement; score: number } | null = null;
  for (const el of candidates) {
    const t = labelOf(el);
    if (!t || t.length > 90) continue;
    let score = 0;
    if (t === n) score = 100;
    else if (t.includes(n)) score = 80 - (t.length - n.length) * 0.1; // el elemento contiene el target
    else if (t.length >= 6 && n.includes(t)) score = 70 - (n.length - t.length) * 0.1; // el target contiene la etiqueta
    else {
      const tTokens = t.split(/\s+/).filter((w) => w.length > 2);
      const hits = tTokens.filter((w) => nTokens.has(w)).length;
      if (hits >= 2 && hits >= tTokens.length * 0.6) score = 40 + hits;
    }
    if (score <= 0) continue;
    if (isVisible(el)) score += 10; // preferimos lo que el cliente ve ahora
    if (!best || score > best.score) best = { el, score };
  }
  return best?.el || null;
}

function findElement(target?: string, anchor?: string): HTMLElement | null {
  if (!target) return null;

  // 0) Ref del mapa de pantalla (lo más preciso: el agente eligió este elemento
  // exacto de lo que el cliente ve ahora). Ej. target="g7".
  const byRef = document.querySelector<HTMLElement>(`[data-guide-ref="${CSS.escape(target)}"]`);
  if (byRef) return byRef;

  const n = normalize(target);

  // Si hay ancla (ej. N° de factura o estado "Error"), buscamos el target DENTRO
  // de la fila/contenedor que contiene ese texto → resalta la fila correcta.
  if (anchor && anchor.trim()) {
    const an = normalize(anchor);
    const hits = Array.from(
      document.querySelectorAll<HTMLElement>('td, th, span, div, p, li, a')
    ).filter((e) => {
      const t = normalize(e.textContent || '');
      return t.includes(an) && t.length <= 140;
    });
    for (const hit of hits) {
      const row = hit.closest<HTMLElement>('tr, [role="row"], li') || hit.parentElement;
      const m = row ? matchIn(row, n) : null;
      if (m) return m;
    }
  }

  return matchIn(document, n);
}

interface Props {
  steps: GuidedStep[];
  onClose: () => void;
  // Si vienen, la guía es ITERATIVA: al terminar los pasos actuales, re-captura la
  // pantalla y le pide al agente el próximo tramo (copiloto paso a paso).
  sessionId?: string;
  goal?: string;
  // Al COMPLETAR la guía (no al cancelar), recibe el recorrido semántico ejecutado
  // → se guarda como playbook reutilizable.
  onComplete?: (journey: GuidedStep[]) => void;
  // Rol del usuario: acota a qué pantallas puede proponer navegar el agente.
  userRole?: string;
}

const MAX_TURNS = 15; // wizards largos + corrección de errores de validación

/** Identidad de un paso, para no repetirlo: misma acción, objetivo e instrucción. */
const stepKey = (s: GuidedStep) => `${s.action}|${s.target || ''}|${s.label || ''}`;

/**
 * Descarta del tramo nuevo los pasos que el cliente YA vio en este recorrido.
 *
 * El agente decide sobre lo que hay en pantalla. Si el cliente toca "Siguiente"
 * sin hacer la acción, la pantalla no cambió y el tramo que vuelve es el mismo:
 * medido en el portal real, el recorrido repetía los cinco campos del formulario
 * y el contador saltaba a "PASO 9 DE 13" con las mismas instrucciones.
 */
function dropSeen(next: GuidedStep[], seen: Set<string>): GuidedStep[] {
  return next.filter((s) => !seen.has(stepKey(s)));
}
// Refs del mapa de pantalla: `g<n>` para elementos interactivos, `e<n>` para los
// mensajes de error visibles. Nunca se muestran al cliente: no significan nada.
const isRef = (t?: string) => !!t && /^[ge]\d+$/.test(t);

/**
 * Recorre los pasos guiados resaltando elementos del portal y navegando entre
 * pantallas. El resaltado usa el teal de marca (#12c4b2) —el mismo acento del
 * resalte de fila de los listados— y el globo de instrucción es una tarjeta del
 * sistema con kicker mono, así el recorrido se ve parte del portal.
 */
export function GuidedTour({ steps, onClose, sessionId, goal, onComplete, userRole }: Props) {
  const navigate = useNavigate();
  const [allSteps, setAllSteps] = useState<GuidedStep[]>(steps);
  // Nota: `seen` se llena al renderizar cada paso (effect de abajo), así que los
  // pasos iniciales entran solos a medida que el cliente los recorre.
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadingNext, setLoadingNext] = useState(false);
  const [guideError, setGuideError] = useState<string | null>(null);
  // El agente repitió pasos ya vistos: hay que hacer la acción para poder seguir.
  const [repeatHint, setRepeatHint] = useState(false);
  // Pasos ya mostrados en este recorrido (para no repetirlos).
  const seen = useRef<Set<string>>(new Set());
  const turns = useRef(0);
  // Recorrido SEMÁNTICO ejecutado (para guardarlo como guía reutilizable).
  const journey = useRef<GuidedStep[]>([]);
  const recorded = useRef<Set<number>>(new Set());
  // Guard de re-entrada: evita avances/fetches múltiples (ej. selects en cascada que
  // disparan varios 'change'). Se libera cuando renderiza el próximo paso.
  const advancing = useRef(false);

  const step = allSteps[index];
  const isLast = index >= allSteps.length - 1;
  const canContinue = !!sessionId && turns.current < MAX_TURNS;
  // Ruta a la que navega este paso, si es que navega. La comparten el effect (que
  // navega) y el render (que decide si dibujar el globo): un paso `navigate` cuyo
  // destino NO se pudo resolver TIENE que mostrarse, si no la guía queda muda.
  const navRoute = step?.action === 'navigate' ? resolveRoute(step.target) : null;

  // EVITA que un modal del portal (Headless UI Dialog) se cierre al tocar el tour.
  // Headless detecta "click afuera" en fase de CAPTURA sobre document, y su único
  // escape es `event.defaultPrevented`. Registramos un listener en CAPTURA sobre
  // window (corre ANTES que el de document) que hace preventDefault() en clicks
  // dentro de nuestra UI -> Headless ve defaultPrevented y NO cierra. Nuestro
  // onClick igual se dispara (preventDefault no lo cancela). Solo vive durante el tour.
  useEffect(() => {
    const guard = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.('[data-support-ui]')) e.preventDefault();
    };
    window.addEventListener('click', guard, true);
    window.addEventListener('touchend', guard, true);
    return () => {
      window.removeEventListener('click', guard, true);
      window.removeEventListener('touchend', guard, true);
    };
  }, []);

  // Graba un paso del recorrido una sola vez (genera target semántico desde el DOM).
  const record = (s: GuidedStep, idx: number, el?: HTMLElement | null) => {
    if (recorded.current.has(idx)) return;
    recorded.current.add(idx);
    const target = isRef(s.target) ? (el?.textContent?.replace(/\s+/g, ' ').trim().slice(0, 60) || s.target) : s.target;
    journey.current.push({ action: s.action, target, value: s.value, label: s.label });
  };

  const finish = useCallback(() => {
    if (onComplete && journey.current.length) onComplete(journey.current);
    onClose();
  }, [onComplete, onClose]);

  const advance = useCallback(async () => {
    if (advancing.current) return; // ya hay un avance en curso (evita dobles fetch)
    advancing.current = true;
    if (!isLast) {
      setIndex((i) => i + 1); // `advancing` se libera al renderizar el nuevo paso
      return;
    }
    // Último paso del tramo: si es iterativa, pedimos el siguiente tramo.
    if (canContinue) {
      setLoadingNext(true);
      setGuideError(null);
      try {
        // Damos un momento a que la pantalla nueva (tras un navigate) cargue/renderice
        // antes de capturar su mapa, así el agente ve los elementos reales.
        await new Promise((r) => setTimeout(r, 600));
        const { data } = await apiClient.post(
          '/support/guide',
          {
            sessionId,
            goal,
            screen: window.location.pathname,
            screenElements: captureScreenMap(),
            // Sin el catálogo el agente inventa el destino de los `navigate`.
            screens: visibleScreens(userRole),
          },
          { timeout: 130000 }
        );
        turns.current += 1;
        const more = (data?.steps ?? []) as GuidedStep[];
        // El cliente puede tocar "Siguiente" SIN hacer la acción. La pantalla no
        // cambió, así que el agente vuelve a indicar exactamente el mismo paso, y
        // antes lo agregábamos igual: la guía repetía "Haz clic en Nuevo Cliente"
        // una y otra vez con el contador subiendo (12 veces en la prueba real).
        // Los pasos que repiten el actual se descartan.
        const fresh = dropSeen(more, seen.current);
        if (!data?.done && fresh.length) {
          setAllSteps((s) => [...s, ...fresh]);
          setIndex((i) => i + 1);
          setLoadingNext(false);
          return;
        }
        if (!data?.done && more.length && !fresh.length) {
          // El agente insiste con el mismo paso: no avanzamos, le pedimos al
          // cliente que haga la acción sobre el elemento resaltado.
          setLoadingNext(false);
          advancing.current = false;
          setRepeatHint(true);
          return;
        }
        // done=true sin más pasos → guía completada.
      } catch (e: any) {
        // NO cerramos en silencio: mostramos el error para no perder la guía.
        setLoadingNext(false);
        advancing.current = false; // liberar para que "Reintentar" funcione
        setGuideError(
          e?.response?.status
            ? `No pude traer el siguiente paso (error ${e.response.status}).`
            : 'No pude traer el siguiente paso (sin conexión con el asistente).'
        );
        return;
      }
      setLoadingNext(false);
    }
    finish();
  }, [isLast, canContinue, sessionId, goal, finish]);

  /**
   * `advance` en un ref para que el effect de abajo dependa SOLO del paso.
   *
   * Sin esto el recorrido se clava en el primer paso `navigate`: `advance` es un
   * useCallback que depende de `finish` → `onComplete`, y el contenedor pasa ese
   * callback inline, así que cambia de identidad en cada render. El effect del
   * paso se re-ejecutaba, su cleanup mataba el setTimeout de 700ms antes de que
   * disparara, y volvía a navegar — de modo que nunca llegaba al paso 2 y, como
   * un paso `navigate` no dibuja UI, el cliente no veía absolutamente nada.
   */
  const advanceRef = useRef(advance);
  useEffect(() => {
    advanceRef.current = advance;
  }, [advance]);

  useEffect(() => {
    if (!step) return;
    let cancelled = false;
    advancing.current = false; // nuevo paso renderizado: listo para el próximo avance
    seen.current.add(stepKey(step));
    setRect(null);
    setNotFound(false);
    setRepeatHint(false);

    // navigate con ruta conocida: cambiamos de pantalla y avanzamos automáticamente.
    if (step.action === 'navigate' && navRoute) {
      record(step, index);
      navigate(navRoute);
      const t = setTimeout(() => !cancelled && advanceRef.current(), 700);
      return () => {
        cancelled = true;
        clearTimeout(t);
      };
    }

    // highlight/click/fill/info: buscamos el elemento (puede tardar en montar).
    let tries = 0;
    let clickCleanup: (() => void) | null = null;
    const tick = () => {
      if (cancelled) return;
      const el = findElement(step.target, step.value);
      if (el) {
        record(step, index, el);
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => !cancelled && setRect(el.getBoundingClientRect()), 250);
        // AUTO-AVANCE en pasos 'click': cuando el cliente toca ESE elemento (ej.
        // "Nuevo Cliente"), avanzamos solos tras un instante para que la pantalla
        // nueva (el modal recién abierto) renderice y el mapa la capture. Así no
        // hay que tocar "Siguiente" después de abrir el modal (era la fricción).
        if (step.action === 'click' || step.action === 'navigate') {
          const onTargetClick = () => {
            if (cancelled) return;
            setTimeout(() => !cancelled && advanceRef.current(), 550);
          };
          el.addEventListener('click', onTargetClick, { once: true });
          clickCleanup = () => el.removeEventListener('click', onTargetClick);
        } else if (
          el instanceof HTMLInputElement ||
          el instanceof HTMLSelectElement ||
          el instanceof HTMLTextAreaElement
        ) {
          // highlight/fill sobre un CAMPO: cuando el cliente lo completa (change),
          // avanzamos al próximo campo. Si es el último paso del tramo, avanzar
          // dispara el fetch del próximo tramo (así, cuando el agente guía un campo
          // a la vez, no queda colgado esperando "Siguiente"). El guard `advancing`
          // evita fetches múltiples por selects en cascada.
          const onFieldChange = () => {
            if (cancelled) return;
            const v = (el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
            if (!v || !v.trim()) return; // no avanzar si quedó vacío
            setTimeout(() => !cancelled && advanceRef.current(), 400);
          };
          el.addEventListener('change', onFieldChange, { once: true });
          clickCleanup = () => el.removeEventListener('change', onFieldChange);
        }
      } else if (tries++ < 40) {
        // Hasta ~6s: el target puede tardar si la pantalla recién navegó y carga datos.
        setTimeout(tick, 150);
      } else {
        setNotFound(true); // sin elemento, igual mostramos la instrucción
      }
    };
    tick();
    return () => {
      cancelled = true;
      clickCleanup?.();
    };
  }, [step, navRoute, navigate]);

  // El cliente avanza con "Siguiente"; además, en pasos 'click' avanzamos solos
  // cuando toca el elemento resaltado (ver effect de arriba). Los `navigate` son
  // transiciones y avanzan solos en su effect.

  // Reposicionar el resaltado al hacer scroll/resize.
  useEffect(() => {
    if (!step || !step.target) return;
    const reposition = () => {
      const el = findElement(step.target, step.value);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [step]);

  if (!step) return null;
  // Un `navigate` que navegó es una transición: no dibuja nada y ya avanzó solo.
  // Uno cuyo destino no se pudo resolver SÍ se dibuja: antes devolvía null igual, y
  // como tampoco tenía forma de avanzar, el cliente tocaba "Guíame en pantalla" y no
  // pasaba absolutamente nada — la guía moría en silencio en el primer paso.
  if (step.action === 'navigate' && navRoute) return null;

  // Nunca mostramos un `ref` crudo (ej. "g48") al cliente: no significa nada.
  const targetIsRef = isRef(step.target);
  const label = step.label || (targetIsRef ? 'Sigue la indicación en pantalla' : step.target) || 'Continúa aquí';
  // Sin elemento resaltado (el agente no lo ubicó): el cliente depende del TEXTO,
  // así que mostramos un globo MÁS GRANDE con la instrucción destacada.
  const big = notFound || !rect;
  // Ancho acotado al viewport: en pantallas angostas nunca se sale.
  const tipW = Math.min(big ? 420 : 328, window.innerWidth - 24);
  // Globo: si el elemento está en la mitad inferior, lo ponemos ARRIBA (para no
  // taparlo ni los botones de abajo); si no, debajo. Si NO hay elemento que anclar,
  // lo fijamos ABAJO (no en el medio, que tapa el contenido y molesta).
  const placeAbove = !!rect && rect.top > window.innerHeight / 2;
  const tipTop = rect
    ? placeAbove
      ? Math.max(12, rect.top - 160)
      : Math.min(rect.bottom + 12, window.innerHeight - 180)
    : window.innerHeight - 220; // abajo
  const tipLeft = rect
    ? Math.min(Math.max(rect.left, 12), window.innerWidth - (tipW + 12))
    : window.innerWidth / 2 - tipW / 2; // centrado horizontal, pegado abajo

  // El recorrido se monta como PORTAL en <body> y no dentro del árbol de la app.
  //
  // Medido en el portal real: con el modal "Nuevo Cliente" abierto, el globo (z-70)
  // quedaba DEBAJO del diálogo (z-50) y un input del formulario interceptaba el
  // clic en "Siguiente" — el cliente no podía avanzar la guía dentro de un modal,
  // que es justo donde más la necesita. El z-index no alcanza: si cualquier
  // ancestro crea un contexto de apilamiento (un `transform` de una animación ya
  // lo hace), el z-70 queda encerrado ahí y no compite con el diálogo. Colgándolo
  // de <body> el problema no puede volver por otro ancestro.
  return createPortal(
    // pointer-events-none en el contenedor: el portal de abajo queda CLICKEABLE
    // (el cliente puede tocar el elemento resaltado). Solo el globo recibe clics.
    <div className="fixed inset-0 z-[9999] pointer-events-none font-display" aria-live="polite" data-support-ui>
      {/* Solo un anillo teal alrededor del elemento — SIN oscurecer la pantalla,
          para no tapar ni bloquear el formulario del portal. No recibe clics. */}
      {rect && (
        <div
          className="pointer-events-none absolute rounded-[10px] ring-[3px] ring-teal-500 ring-offset-2 transition-all"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
            boxShadow: '0 0 0 4px rgba(18,196,178,.22)',
          }}
        />
      )}

      {/* Globo con la instrucción (único elemento que recibe clics). stopPropagation
          en mousedown: evita que un modal del portal (Headless UI Dialog) lo tome como
          clic-afuera y se cierre al tocar Siguiente. */}
      <div
        className={`pointer-events-auto absolute max-w-[92vw] rounded-[14px] border border-[#ececec] bg-white shadow-[0_30px_70px_-20px_rgba(10,34,51,.35)] dark:border-secondary-700 dark:bg-secondary-800 ${big ? 'p-5' : 'p-4'}`}
        style={{ top: tipTop, left: tipLeft, width: tipW }}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className={`mb-1.5 ${METRIC_LABEL}`}>
          PASO {index + 1} DE {allSteps.length}
        </div>
        <Markdown
          text={label}
          className={`text-ink-850 dark:text-white ${big ? 'text-[17px] font-bold leading-snug tracking-[-0.01em]' : 'text-[13.5px] font-medium leading-[1.5]'}`}
        />
        {notFound && (
          <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#eef0f2] bg-[#fbfbfc] p-3 dark:border-secondary-700 dark:bg-secondary-900">
            <svg className="mt-0.5 h-[18px] w-[18px] shrink-0 text-teal-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <p className="text-[13px] leading-[1.5] text-secondary-600 dark:text-secondary-300">
              {step.target && !targetIsRef ? (
                <>
                  Busca <span className="font-semibold text-ink-850 dark:text-white">«{step.target}»</span> en esta pantalla y luego toca{' '}
                  <span className="font-semibold">Siguiente</span>.
                </>
              ) : (
                'Sigue esta indicación en la pantalla y luego toca Siguiente.'
              )}
            </p>
          </div>
        )}
        {loadingNext && (
          <p className="mt-2 flex items-center gap-2 text-[12px] font-medium text-teal-700">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
            Preparando el siguiente paso…
          </p>
        )}
        {guideError && <p className="mt-2 text-[12px] font-medium text-danger-600">{guideError}</p>}
        {repeatHint && !guideError && (
          <p className="mt-2 text-[12px] font-medium text-teal-700">
            Realiza esta acción en la pantalla para continuar.
          </p>
        )}
        <div className="mt-3.5 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-[10px] px-3 py-2 text-[13px] font-medium text-secondary-500 transition-colors hover:bg-[#f6f6f7] hover:text-ink-850 dark:hover:bg-secondary-700 dark:hover:text-white"
          >
            Cerrar
          </button>
          <button
            onClick={() => { setGuideError(null); advance(); }}
            disabled={loadingNext}
            className={`${CTA} py-2 text-[13px]`}
          >
            {guideError ? 'Reintentar' : isLast ? (canContinue ? 'Siguiente' : 'Listo') : 'Siguiente'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
